<template>
  <vx-card class="mt-base" :title="activityTitle">
    <h4 class="mb-5">The Colour Wheel</h4>
    <p class="mb-5">
      Whatever job you’re interested in doing in the creative arts, chances are good that having an
      understanding of the basics of colour theory will help.
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
        <p class="mb-5">
          For example, colour is an important element in composition for photographers. For graphic
          designers, colour can be used to draw attention, while an interior designer might select
          specific colours to create a certain mood.
        </p>
        <p class="mb-5">
          One of the most challenging aspects of working with colours is knowing what colours go
          well together. This is where a colour wheel can help.
        </p>
      </div>
      <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/creativeindustries/colour_wheel_300.jpg"
          class="img-responsive mx-auto"
          alt=""
        />
      </div>
    </div>
    <p class="mb-5">
      Colour wheels reveal the relationships between different colours in the spectrum. They help us
      find which colours ‘harmonize’ together, that is, colour wheels help us to see which colours
      go well together.
    </p>
    <p class="mb-5">
      In a colour wheel, all the colours of the spectrum are laid out on a circle allowing us to see
      how they relate to each other.
    </p>
    <p class="mb-5">There are different ways to make colour combinations, for example:</p>

    <h4 class="mb-5">Complementary</h4>
    <p class="mb-5">
      Complementary colours are two colours on opposite sides of the colour wheel. This type of
      colour combination creates a strong contrast and has a high impact.
    </p>
    <h4 class="mb-5">Monochromatic</h4>
    <p class="mb-5">
      Monochromatic colours are different tones that come from the same angle on the colour wheel.
      They go together as they come from the same colour ‘family’.
    </p>
    <h4 class="mb-5">Analogous</h4>
    <p class="mb-5">
      Analogous colours are colours that are next to each other on the colour wheel. Usually, one
      dominant colour is chosen, with the other colours used as accents.
    </p>
    <h4 class="mb-5">Triadic</h4>
    <p class="mb-5">
      Triadic colour schemes are made up of three colours that are evenly spaced around the colour
      wheel, offering bold contrasts.
    </p>
    <h4 class="mb-5">Tetradic</h4>
    <p class="mb-5">
      Tetradic colour combinations employ four colours that are evenly spaced around the wheel. This
      works best when one colour is the dominant one and the others are used as accents.
    </p>
    <p class="mb-5">
      To learn more about basic colour theory, check out this short and fun video - don’t worry,
      it’s relevant to more than just graphic design!
    </p>

    <h4 class="mb-5">Beginning Graphic Design: Colour</h4>
    <p class="mb-5">Watch the video below to get a good overview of colour theory.</p>
    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

    <h4 class="mb-5">Experiment with the Colour Wheel</h4>
    <p class="mb-5">
      One of the best ways to gain an understanding of how various colour combinations work is to
      play around with an online colour wheel and experiment.
    </p>
    <p class="mb-5">
      Follow this link (click the image below) to access an online colour wheel. First, start by
      choosing a colour on the wheel. Then, select a colour combination type from the drop-down menu
      and you’ll see a section of colours and what’s known as their Hex Colour Code.
    </p>
    <p class="mb-5">
      The <i>Hex Colour Code</i> is just a system designed to let a computer know how much red,
      green, and blue to mix to produce each colour.
    </p>

    <a href="https://www.canva.com/colors/color-wheel/" target="_blank">
      <centered-image-wrapper
        alt="Open the Canva colour wheel (opens in a new tab)"
        :image="
          require('@/assets/images/experiences/creativeindustries/canva_colour_wheel_button.jpg')
        "
      />
    </a>

    <h4 class="mb-5">Your Task: Using The Colour Method</h4>
    <p class="mb-5">
      Just as we exercise our body and drill movements to become better at sports, we can exercise
      our eyes to recognise the differences in various aspects of colour and become better at
      coordinating colours and choosing combinations of colours.
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
        <p class="mb-5">
          The <i>Colour Method</i> is a fun way to do this. This online tool is a series of
          colour-matching games that use an interactive colour wheel to test your eyes in 6
          different areas:
        </p>
        <ul class="default-list">
          <li>Hue (the shade of a colour)</li>
          <li>Saturation (how vivid or intense a colour is)</li>
          <li>Complementary</li>
          <li>Analogous</li>
          <li>Triadic</li>
          <li>Tetradic</li>
        </ul>
        <p class="mb-5">
          Use the buttons on the right to access the Colour Method so you can complete the activity
          and also a video that explains what you need to do.
        </p>
      </div>
      <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
        <a href="https://color.method.ac/" target="_blank"
          ><img
            src="@/assets/images/experiences/creativeindustries/access_colour_method_button.jpg"
            alt="Open the Colour Method activity (opens in a new tab)"
            class="img-responsive mx-auto mb-10"
        /></a>
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Play video: How to complete this activity"
          @click="showVideoPopup(videos[2].video)"
        >
          <img :src="videos[2].image" class="img-responsive mx-auto cursor-pointer" alt="" />
        </button>
      </div>
    </div>

    <p class="mb-5">
      In each of the tasks, you’ll be asked to match specific colours using your mouse or touchpad.
      To make things more interesting (and challenging!), there’s an onscreen timer ticking away as
      you search for the matching colours.
    </p>
    <p class="mb-5">
      The tasks get harder as you go and be warned, this game can get highly addictive. But, even
      though it’s a lot of fun, it will tune up your ability to match colours as well as get a
      better understanding of the terms such as hue, saturation, complementary, analogous, triadic,
      and tetradic.
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5">At the end of the game, you will be given a number of scores:</p>
        <ul class="default-list">
          <li>Overall</li>
          <li>Hue</li>
          <li>Saturation</li>
          <li>Complementary</li>
          <li>Analogous</li>
          <li>Triadic</li>
          <li>Tetradic</li>
        </ul>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/creativeindustries/colour_match_score.jpg"
          class="img-responsive mx-auto mb-10"
          alt="Example Colour Method results screen: a large Overall score of 4.7 at the top, with a row of coloured boxes below showing Hue 7, Saturation 6, Complementary 5, Analogous 3, Triadic 2 and Tetradic 5."
        />
      </div>
    </div>
    <p class="mb-5">
      You need to record all your scores twice, once when you try the activity without any practice
      and then a second time after you have tried the activity a number of times and practiced it.
    </p>

    <p class="mb-5"><strong>1. My scores on the first attempt with no practice:</strong></p>
    <div class="vx-row">
      <div class="vx-col flex-1 sm:w-1/4 xs:w-1/2 my-5" v-for="index in 7" :key="index">
        <p class="mb-2">{{ scores[index - 1] }}</p>
        <text-block v-model="data['question_1_' + index]" :editable="isEditable" pclass="my-2" />
      </div>
    </div>

    <p class="mb-5">
      <strong>2. My scores after practicing the activity a number of times:</strong>
    </p>
    <div class="vx-row">
      <div class="vx-col flex-1 sm:w-1/4 xs:w-1/2 my-5" v-for="index in 7" :key="index">
        <p class="mb-2">{{ scores[index - 1] }}</p>
        <text-block v-model="data['question_2_' + index]" :editable="isEditable" pclass="my-2" />
      </div>
    </div>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/creativeindustries/colour_design_splash.jpg'),
          video: 'https://www.youtube.com/embed/_2LLXnUdUIc',
        },
        2: {
          image: require('@/assets/images/experiences/creativeindustries/how_to_complete_activity_1_trigger.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            '/content/creativeindustries/how_to_complete_activity_1.mp4',
        },
      },
      scores: ['Overall', 'Hue', 'Saturation', 'Complementary', 'Analogous', 'Triadic', 'Tetradic'],
      login_type: localStorage.getItem('login_type'),
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
};
</script>

<style scoped>
.w-1\/8 {
  width: 12.5% !important;
}
</style>
